import { Card, Radio, Tag, Select, DatePicker, Button, Tooltip, Input } from "antd";
import Table, { ColumnsType } from "antd/lib/table";

import styles from "../MasterManagement/index.module.less";
import { TableHelper } from "../../util/TableHelper";
import { RequestStatus, ParkingRequestTypeConstant } from "../../constants/DatabaseConstants/ParkingConstants";
import Loading from "../common/Loading";
import useParkingHistory from "./index.hook";
import LocalStorageUtil from "../../util/LocalStorageUtil";
import { LocalStorageConstants } from "../../constants/ApplicationConstants/LocalStorageConstants";
import Authentication from "../../util/Authentication";
import { Modules, Operations } from "../../constants/DatabaseConstants/MasterConstant";
import { General } from "../../util/General";

import type { UserInterface } from "../../shared/interface/auth/User.interface";
import type { LogParkingHistoryRecord } from "../../shared/interface/parking.interface";
import CircularProgressBar from "../common/CircularProgressBar";
import { FilePdfOutlined } from "@ant-design/icons";
import moment from "moment-timezone";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ParkingHistory = () => {
  /* CONSTANT */
  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  const config = useParkingHistory();

  const renderText = (text: string | number) => {
    return <div className="wordWrapWordBreak">{TableHelper.getValue(text)}</div>;
  };

  const renderNumberValue = (text: string | number) => {
    return <div className="gx-text-right">{TableHelper.getValue(text)}</div>;
  };

  const renderCompletionStatus = (text: string | null | undefined) => {
    let color;

    switch (text) {
      case RequestStatus.REJECTED:
        color = "orange";
        break;
      case RequestStatus.CANCELLED:
        color = "error";
        break;
      case RequestStatus.UNABLE_TO_IDENTIFY:
        color = "black";
        break;
      case RequestStatus.REQUEST_SENT:
        color = "green";
        break;
      case RequestStatus.CANCELLATION_REQUEST_SENT:
        color = "green";
        break;
      case RequestStatus.REQUEST_ACK_RECEIVED:
        color = "green";
        break;
      case RequestStatus.CANCELLATION_REQUEST_ACK_RECEIVED:
        color = "green";
        break;

      default:
        color = "success";
    }

    return <Tag color={color}>{text ?? ""}</Tag>;
  };

  const columns: ColumnsType<LogParkingHistoryRecord> = [
    {
      align: "left",
      title: "Sr No.",
      dataIndex: "sr_no",
      key: "sr_no",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.valueCompare("sr_no")
      },
      width: "5%"
    },
    {
      align: "left",
      title: "Username",
      dataIndex: "full_name",
      key: "full_name",
      render: (text, record) => <div className={record?.id === config.recordID ? `wordWrapWordBreak gx-link entity-name-selected` : `wordWrapWordBreak`}>{TableHelper.getValue(text)}</div>,
      sorter: {
        compare: TableHelper.valueCompare("full_name")
      }
    },
    {
      align: "left",
      title: "KeyFob",
      dataIndex: "key_fob_display_value",
      key: "key_fob_display_value",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("key_fob")
      }
    },
    {
      align: "left",
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("location")
      }
    },
    {
      align: "left",
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("client")
      }
    },
    {
      align: "left",
      title: "Request Status",
      dataIndex: "requestStatus",
      key: "requestStatus",
      sorter: {
        compare: TableHelper.valueCompare("requestStatus")
      },
      render: renderCompletionStatus
    },
    {
      align: "left",
      title: "Request ACK Status",
      dataIndex: "requestACKStatus",
      key: "requestACKStatus",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("requestACKStatus")
      }
    },
    {
      align: "left",
      title: "Scheduled Date-Time",
      dataIndex: "scheduled_date_time",
      key: "scheduled_date_time",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.dateSorter("scheduled_date_time")
      }
    },
    {
      align: "left",
      title: "Created On",
      dataIndex: "created_on",
      key: "created_on",
      fixed: "right",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.dateSorter("created_on")
      }
    }
  ];

  return (
    <Card
      className="gx-card card--height"
      title={
        <div className={`gx-title-container`}>
          <Radio.Group onChange={(e) => config.handleModeChange(e.target.value)} value={config.mode}>
            <Radio.Button className={styles["button-font-size"]} value={ParkingRequestTypeConstant.RETRIEVAL}>
              {ParkingRequestTypeConstant.RETRIEVAL}
            </Radio.Button>
            <Radio.Button className={styles["button-font-size"]} value={ParkingRequestTypeConstant.BOOKING}>
              {ParkingRequestTypeConstant.BOOKING}
            </Radio.Button>
            <Radio.Button className={styles["button-font-size"]} value={ParkingRequestTypeConstant.STORAGE}>
              {ParkingRequestTypeConstant.STORAGE}
            </Radio.Button>
          </Radio.Group>

          {/* <div className={`${styles["extra-container"]}`}>
            <Input.Search className={`gx-mb-0 search-component__width`} allowClear placeholder={`Search...`} value={config.searchedText} onChange={(e) => config.handleChangeSearch(e.target.value)} onSearch={config.handleChangeOnSearch} />
          </div> */}
        </div>
      }
    >
      <div className={`gx-parking-history-filters`}>
        {/* Select KeyFob */}
        <Select allowClear showSearch value={config?.selectedKeyFob} listHeight={200} onChange={(value) => config?.handleChangeFilter("keyFob", value)} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} className={styles["dropdown__selector_small"]} placeholder="Select KeyFob">
          {config.keyFobList?.map((item, index) => {
            if (item == null) return null;
            return (
              <Option key={index} value={item?.id}>
                {item?.keyFobToken}
              </Option>
            );
          })}
        </Select>

        {/* Select Request Status */}
        <Select allowClear showSearch value={config?.selectedCompletionStatus} listHeight={200} onChange={(value) => config?.handleChangeFilter("status", value)} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} className={styles["dropdown__selector_small"]} placeholder="Select Request Status">
          {config.completionStatusList?.map((item) => {
            if (item == null) return null;
            return (
              <Option key={item} value={item}>
                {item}
              </Option>
            );
          })}
        </Select>

        {/* Select Req ACK Status */}
        <Select allowClear showSearch value={config?.selectedReqAckStatus} listHeight={200} onChange={(value) => config?.handleChangeFilter("reqAckStatus", value)} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} className={styles["dropdown__selector"]} placeholder="Select Req ACK Status">
          {config.reqAckStatusList?.map((item, index) => {
            if (item == null) return null;
            return (
              <Option key={index} value={item?.id}>
                {item?.request_ack_description}
              </Option>
            );
          })}
        </Select>

        {/* DatePicker */}
        <RangePicker value={config.dateRange.fromDate && config.dateRange.toDate ? [moment(config.dateRange.fromDate, "DD-MM-YYYY HH:mm"), moment(config.dateRange.toDate, "DD-MM-YYYY HH:mm")] : null} getPopupContainer={(trigger) => trigger.parentElement || document.body} className={styles["datepicker__width"]} showTime={{ format: "HH:mm" }} format="DD-MM-YYYY HH:mm" onChange={config.handleChangeDatePicker} disabledDate={General.disabledDate} disabledTime={General.disabledRangeTime} />

        <Button className={`button-gradiant gx-mb-0 gx-mr-0`} onClick={config.handleGo}>
          Go
        </Button>
        <Tooltip title="Download PDF File">
          <div onClick={config.historyList?.length === 0 ? undefined : config.handleExportPDFFile}>{config.historyList?.length === 0 ? <FilePdfOutlined className={styles["pdf-icon-disabled"]} /> : config?.pdfIconLoading ? <CircularProgressBar /> : <FilePdfOutlined className={styles["pdf-icon"]} />}</div>
        </Tooltip>
      </div>

      {config.loading && <Loading />}

      {!config.loading && Authentication.hasModuleAndOperationAccess(userData, Modules.SCHEDULE_LIST, Operations.READ) && (
        <Table //
          className="gx-table-responsive"
          size="small"
          columns={columns}
          dataSource={config.historyList}
          pagination={config?.pagination}
          bordered
          onChange={config.handleChangePagination}
          rowKey={(record) => record.sr_no}
        />
      )}
    </Card>
  );
};
export default ParkingHistory;
